// Park saves the work (2026-10-27, docs/decisions/2026-10-27-park-saves-
// work.md, docs/contact-view.md "Parking"): the CSR's work in progress on a
// contact, saved on Park and autosaved a few seconds after the last edit,
// restored when the contact is opened again. Pure helpers, kept out of
// SuggestionPanel.jsx so node --test can cover them.
//
// The payload holds customer text (the reply, addresses in step inputs). It
// is sent only to /api/parks/conversations/:id (and /work), never logged.
//
// Step inputs are saved as the panel holds them (actionInputs, keyed by step
// index), whatever fields a step has, so a step that gains an input needs no
// change here. Restored onto a different suggestion (the one unpark
// regenerated), each saved step is matched to the new list by its identity
// (@cxhub/shared's stepKey(): type, kind, target) and its inputs follow it.

import { savedStepIndexMap, etDateTime } from '@cxhub/shared'

export const WORK_PAYLOAD_VERSION = 1
// A few seconds after the last edit.
export const WORK_AUTOSAVE_DELAY_MS = 3000
// Attachment bytes saved with the work, in data-URL characters; above it only
// the names are kept, and the restored line says to attach them again.
export const WORK_ATTACHMENTS_MAX_CHARS = 4 * 1024 * 1024

const stepShape = (a) => ({ type: a?.type ?? null, kind: a?.params?.kind ?? null, targetSystem: a?.targetSystem ?? null, label: a?.label ?? null })

// The steps a Process already ran (stepStates 'success'), with their result,
// so a restore marks them processed and never offers them again.
export function processedStepsFrom(stepStates = {}, actions = []) {
  return Object.entries(stepStates ?? {})
    .filter(([, st]) => st?.status === 'success')
    .map(([i, st]) => ({ index: Number(i), ...stepShape(actions?.[Number(i)]), result: st.result ?? null }))
    .sort((a, b) => a.index - b.index)
}

function attachmentsForSave(attachments = []) {
  const list = (attachments ?? []).map((a) => ({ id: a.id ?? null, name: a.name ?? null, size: a.size ?? null, dataUrl: a.dataUrl ?? null }))
  const total = list.reduce((n, a) => n + (a.dataUrl?.length ?? 0), 0)
  if (total <= WORK_ATTACHMENTS_MAX_CHARS) return { attachments: list, attachmentsDropped: false }
  return { attachments: list.map((a) => ({ ...a, dataUrl: null })), attachmentsDropped: true }
}

// The reply body and every step's message box of a payload, each through
// its function (2026-10-30): the signature token on save, the opening CSR's
// name on restore. Everything else is untouched.
export function mapWorkText(payload, { reply = null, step = null } = {}) {
  if (!payload) return payload
  const out = { ...payload }
  if (reply && typeof payload.reply?.body === 'string') out.reply = { ...payload.reply, body: reply(payload.reply.body) }
  if (step && payload.actionInputs) {
    out.actionInputs = Object.fromEntries(Object.entries(payload.actionInputs).map(([k, v]) => (
      [k, v && typeof v.editedMessage === 'string' ? { ...v, editedMessage: step(v.editedMessage) } : v]
    )))
  }
  return out
}

// Everything the CSR has done on the contact so far. tokenize (2026-10-30):
// the saving CSR's resolved signature back to [AGENT_FIRST_NAME]
// (@cxhub/shared's tokenizeAgentName()), so the saved work never holds a
// name and a restore signs as whoever opens it.
export function buildWorkPayload({
  suggestion = null, draft = '', from = '', to = '', cc = null, subject = '', replyMode = null, startNewContact = false,
  insertedTemplate = null, attachments = [], actionInputs = {}, uncheckedSteps = [], notNeededSteps = [],
  taskChoice = null, taskChoiceDate = '', partLinks = {}, stepStates = {}, tokenize = null,
} = {}) {
  const actions = suggestion?.proposedActions ?? []
  const payload = {
    v: WORK_PAYLOAD_VERSION,
    reply: { from, to, cc, subject, body: draft, replyMode, startNewContact: Boolean(startNewContact), template: insertedTemplate ?? null, ...attachmentsForSave(attachments) },
    steps: actions.map(stepShape),
    actionInputs: actionInputs ?? {},
    uncheckedSteps: [...(uncheckedSteps ?? [])],
    notNeededSteps: [...(notNeededSteps ?? [])],
    taskChoice: taskChoice ? { choice: taskChoice, date: taskChoiceDate || null } : null,
    partLinks: Object.fromEntries(Object.entries(partLinks ?? {}).map(([k, v]) => [k, { ...v, busy: false }])),
    processedSteps: processedStepsFrom(stepStates, actions),
  }
  return tokenize ? mapWorkText(payload, { reply: tokenize, step: tokenize }) : payload
}

// --- Only real work (2026-10-30) ---------------------------------------------
// docs/decisions/2026-10-30-restored-work-signature-owner.md. The autosave
// runs only after the person's own action on the panel (typing, a choice, a
// checkbox, an attachment, a step marked not needed), never because state
// differs after a load, a reseed, a restore or name resolution.

// Whether an input / change / click event on the panel is the person's work:
// not inside a dialog (a template search, Send Feedback, a confirm), except
// a step card the Manual Panel's window holds.
export function isWorkEventTarget(target) {
  if (!target || typeof target.closest !== 'function') return false
  if (target.closest('[data-testid="manual-step-slot"]')) return true
  return !target.closest('[role="dialog"]')
}

// A click that picks something from a list (a reason code, a disposition, an
// item from a menu): MUI's Select and Autocomplete call onChange without a
// DOM change event.
export function isWorkChoiceClick(target) {
  return isWorkEventTarget(target) && Boolean(target.closest('[role="option"]'))
}

// A key that edits the box it was typed in (the rich reply editor edits
// without an input event for Enter or Backspace).
export function isEditingKey(e) {
  const t = e?.target
  const editable = t && (t.isContentEditable || t.tagName === 'TEXTAREA' || (t.tagName === 'INPUT' && !['checkbox', 'radio', 'button'].includes(t.type)))
  if (!editable || t.readOnly) return false
  const key = String(e.key ?? '')
  if (e.ctrlKey || e.metaKey) return ['v', 'x', 'z', 'y'].includes(key.toLowerCase())
  if (e.altKey) return false
  return key.length === 1 || key === 'Backspace' || key === 'Delete' || key === 'Enter'
}

const blankish = (v) => v == null || v === '' || v === false
  || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)

function sameValue(a, b, normalize) {
  if (blankish(a) && blankish(b)) return true
  if (typeof a === 'string' && typeof b === 'string') return normalize(a) === normalize(b)
  return JSON.stringify(a) === JSON.stringify(b)
}

// The step checkboxes as the suggestion starts them (every step checked),
// with the steps already processed unchecked: they stay done and are never
// offered again. "Start from the AI draft instead" (2026-10-30).
export function defaultUncheckedSteps(stepStates = {}) {
  return Object.entries(stepStates ?? {})
    .filter(([, st]) => st?.status === 'success')
    .map(([i]) => Number(i))
    .sort((a, b) => a - b)
}

// Text as the "differs from the draft" check compares it: tags, entities and
// spacing gone (the email editor re-serializes HTML on load).
export function comparableText(text) {
  return String(text ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
}

// Whether saved work holds anything the AI draft and the step defaults would
// not load by themselves, compared against `base` (the suggestion it was
// saved on). freshDraft: the box text that suggestion loads; defaults: its
// step defaults (reseedActionInputs()); baseUnchecked / baseTaskChoice: its
// recorded checkboxes and path choice ({ choice, date }); normalize: text as
// compared (the signature as the token, then comparableText()). Saved work
// that equals the draft is nobody's work: no "Restored" line, and the row
// goes. Without a base to compare with, it counts as work.
export function workDiffersFromDraft(work, { base = null, freshDraft = '', defaults = {}, baseUnchecked = [], baseTaskChoice = null, normalize = comparableText } = {}) {
  const p = work?.payload ?? {}
  if (!base) return true
  if ((p.processedSteps ?? []).length || (p.notNeededSteps ?? []).length) return true
  const reply = p.reply ?? {}
  if ((reply.attachments ?? []).length || reply.template || reply.cc != null || reply.startNewContact) return true
  if (typeof reply.body === 'string' && !sameValue(reply.body, freshDraft ?? '', normalize)) return true
  if (Object.values(p.partLinks ?? {}).some((v) => v?.link)) return true
  const map = stepIndexMap(work, base)
  for (const [k, input] of Object.entries(p.actionInputs ?? {})) {
    const j = map[k]
    if (j == null) {
      if (!blankish(input)) return true
      continue
    }
    const d = defaults?.[j] ?? {}
    const fields = new Set([...Object.keys(input ?? {}), ...Object.keys(d)])
    for (const f of fields) if (!sameValue(input?.[f], d[f], normalize)) return true
  }
  const unchecked = remapList(p.uncheckedSteps, map).sort((a, b) => a - b)
  if (JSON.stringify(unchecked) !== JSON.stringify([...(baseUnchecked ?? [])].sort((a, b) => a - b))) return true
  const savedChoice = p.taskChoice ?? null
  if ((savedChoice?.choice ?? null) !== (baseTaskChoice?.choice ?? null)) return true
  if ((savedChoice?.date || null) !== (baseTaskChoice?.date || null)) return true
  return false
}

// What the autosave compares, so an unchanged panel is never re-saved
// (attachment bytes left out: their names and sizes stand in).
export function workFingerprint(payload) {
  if (!payload) return ''
  const reply = payload.reply ? { ...payload.reply, attachments: (payload.reply.attachments ?? []).map((a) => [a.name, a.size]) } : null
  return JSON.stringify({ ...payload, reply })
}

// { [savedIndex]: targetIndex } for a restore onto `target`
// (@cxhub/shared's savedStepIndexMap()).
export function stepIndexMap(work, target) {
  return savedStepIndexMap(work, target)
}

const remapList = (list, map) => [...new Set((list ?? []).map((i) => map[i]).filter((i) => i != null))]
const remapKeys = (obj, map) => Object.fromEntries(Object.entries(obj ?? {}).map(([k, v]) => [map[k], v]).filter(([k]) => k != null && k !== 'undefined'))

// The panel state a restore puts back, onto `target` (the suggestion the
// panel works on now). keepSteps: whether the saved step inputs replace the
// target's defaults (false: "Use new suggestion" on the steps — the reply and
// the email fields are restored either way unless keepReply is false).
// Returns { draft, reply, actionInputs, uncheckedSteps, notNeededSteps,
// taskChoice, partLinks, processed: { [targetIndex]: result }, map }.
export function restoredWorkFor(work, target, { keepSteps = true, keepReply = true } = {}) {
  const p = work?.payload ?? {}
  const map = stepIndexMap(work, target)
  const processed = {}
  for (const s of p.processedSteps ?? []) {
    const j = map[s.index]
    if (j != null) processed[j] = s.result ?? null
  }
  return {
    map,
    draft: keepReply && typeof p.reply?.body === 'string' ? p.reply.body : null,
    reply: keepReply ? (p.reply ?? null) : null,
    actionInputs: keepSteps ? remapKeys(p.actionInputs, map) : {},
    uncheckedSteps: keepSteps ? remapList(p.uncheckedSteps, map) : [],
    notNeededSteps: keepSteps ? remapList(p.notNeededSteps, map) : [],
    taskChoice: keepSteps ? (p.taskChoice ?? null) : null,
    partLinks: keepSteps ? remapKeys(p.partLinks, map) : {},
    processed,
  }
}

// Whether a saved work record should be restored at all: not when the
// suggestion it was done on was decided by a person since (the record is
// stale), and only onto a pending suggestion.
export function shouldRestoreWork(work, { latest = null, base = null } = {}) {
  if (!work || !latest || latest.status !== 'pending') return false
  if (base && base.status !== 'pending' && base.decidedBy && base.decidedBy !== 'system:superseded') return false
  return true
}
// The "Since you parked this" panel's items (ParkComparePanel.jsx): the
// reply, and each step by kind and index (a removed step by its index on the
// parked suggestion).
export const REPLY_ITEM = 'reply'
export const stepItemId = (kind, index) => `steps:${kind}:${index}`

// Every item the CSR can choose on, in the panel's order.
export function compareItems(compare) {
  if (!compare) return []
  const items = []
  for (const s of compare.steps?.changed ?? []) items.push(stepItemId('changed', s.index))
  for (const s of compare.steps?.added ?? []) items.push(stepItemId('added', s.index))
  for (const s of compare.steps?.removed ?? []) items.push(stepItemId('removed', s.index))
  if (compare.reply?.changed) items.push(REPLY_ITEM)
  return items
}


// The panel's changes (2026-10-28): one card per area, Steps then Reply, each
// with one choice that covers all its items. [{ key, label, items }].
export const PARK_CHANGE_STEPS = 'steps'
export const PARK_CHANGE_REPLY = 'reply'
export function compareChanges(compare) {
  const items = compareItems(compare)
  const stepItems = items.filter((id) => id !== REPLY_ITEM)
  const out = []
  if (stepItems.length) out.push({ key: PARK_CHANGE_STEPS, label: 'Steps', items: stepItems })
  if (items.includes(REPLY_ITEM)) out.push({ key: PARK_CHANGE_REPLY, label: 'Reply', items: [REPLY_ITEM] })
  return out
}

// A change's choice: the one value all its items share, else null.
export function changeChoice(change, choices = {}) {
  const values = new Set((change?.items ?? []).map((id) => choices?.[id] ?? null))
  return values.size === 1 ? [...values][0] : null
}

// Whether every item of a change has a pick (the same or, for the steps,
// one per step: "mixed").
export function changeDecided(change, choices = {}) {
  const items = change?.items ?? []
  return items.length > 0 && items.every((id) => choices?.[id] != null)
}

// The choices with these items set (value) or cleared (null, their Undo).
// items: a change's items (its card-level pair) or one step's id.
export function chooseItems(choices = {}, items = [], value) {
  const next = { ...choices }
  for (const id of items ?? []) {
    if (value == null) delete next[id]
    else next[id] = value
  }
  return next
}
export const chooseChange = (choices, change, value) => chooseItems(choices, change?.items, value)

// Every change's items set to one value: the bottom row's "Keep all my saved
// work" / "Use all new suggestions" (2026-10-28: they pick, Done applies).
export function chooseAll(compare, value) {
  return Object.fromEntries(compareItems(compare).map((id) => [id, value]))
}

// Whether every change has a pick: then "Done" shows, and applies them.
export function allChangesDecided(compare, choices = {}) {
  const changes = compareChanges(compare)
  return changes.length > 0 && changes.every((c) => changeDecided(c, choices))
}

// Whether the compare has nothing to choose on: only order facts or a new
// customer message changed. Then nothing asks (2026-10-28): the work stays
// and the info strip lists what changed.
export const compareNeedsChoice = (compare) => compareItems(compare).length > 0

// The info strip's lines for that case.
export const PARK_FACTS_ONLY_TEXT = 'Checked for changes: your steps and reply are the same, so your work stays. What changed since you parked this:'
export function parkFactLines(compare) {
  return [
    ...(compare?.messages ?? []).map((m) => `New customer message, ${etDateTime(m.at)}: ${m.text}`),
    ...(compare?.facts ?? []).map((f) => f.text),
  ]
}

// "Change 1 of 2 · Steps", or just "Steps" when there is only one change.
export function changeHeading(change, n, total) {
  return total > 1 ? `Change ${n} of ${total} · ${change.label}` : change.label
}

// "1 change" / "2 changes".
export const changesCount = (n) => `${n} change${n === 1 ? '' : 's'}`

// The bottom row's label and buttons: they apply to every change.
export const applyToAllLabel = (n) => `Apply to all ${changesCount(n)}:`
export const PARK_KEEP_ALL_LABEL = 'Keep all my saved work'
export const PARK_USE_NEW_ALL_LABEL = 'Use all new suggestions'
// A card's choice once made.
export const PARK_KEPT_TEXT = 'Kept your saved work'
export const PARK_USING_NEW_TEXT = 'Using the new suggestion'
export const PARK_NO_NEW_REPLY_TEXT = 'The new suggestion has no reply.'

// Whether the new suggestion's reply is empty (no text once tags and
// whitespace are gone).
export function isEmptyReply(text) {
  return !String(text ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim()
}
export const PARK_DONE_LABEL = 'Done'
export const PARK_PICKED_PER_STEP_TEXT = 'Picked per step'

// Whether the info strip hides on the CSR's work (an edit, a step's checkbox,
// Process). Not while it lists what changed (the facts-only case,
// 2026-10-28): that stays until its x.
export const parkStripHidesOnWork = (changes) => !(changes?.length > 0)

// Whether the bottom row's "Keep all my saved work" / "Use all new
// suggestions" show: not once every change has a pick (2026-10-28), so Done
// is the only primary action left; an Undo brings them back.
export const showApplyToAll = (compare, choices = {}) => compareChanges(compare).length > 1 && !allChangesDecided(compare, choices)
